import { useState, useRef, useEffect } from 'react';

import { HomeIcon } from '@/assets/Icons/HomeIcon/HomeIcon';
import { ChevronDownIcon } from '@/assets/Icons/ChevronDownIcon/ChevronDownIcon';
import { MenuIcon } from '@/assets/Icons/MenuIcon/MenuIcon';
import { CloseIcon } from '@/assets/Icons/CloseIcon/CloseIcon';
import { FacebookIcon } from '@/assets/Icons/FacebookIcon/FacebookIcon';
import { InstagramIcon } from '@/assets/Icons/InstagramIcon/InstagramIcon';
import { LinkedInIcon } from '@/assets/Icons/LinkedInIcon/LinkedInIcon';
import { LoginIcon } from '@/assets/Icons/LoginIcon/LoginIcon';
import { SearchAutocomplete } from '@/shared/components/Search/SearchAutocomplete/SearchAutocomplete';
import { B2B_PORTAL_URL } from '@/shared/utils/constants';
import { useFetch } from '@/shared/hooks/useFetch/useFetch';
import type { ApiInsightsMenuResponse } from '@/shared/types/Insights';

//To be refactored Social links, dropdown menu, icons, links
interface NavLink {
  label: string;
  href: string;
  hasDropdown: boolean;
  dropdownItems?: { label: string; href: string }[];
  dropdownColumns?: boolean;
}

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { data: menuData } = useFetch<ApiInsightsMenuResponse>({
    url: '/v1/public/insights/menu',
  });
  const insightsMenu = menuData?.data ?? [];

  const links: NavLink[] = [
    { label: 'news', href: '/news', hasDropdown: false },
    {
      label: 'insights',
      href: '/insights',
      hasDropdown: insightsMenu.length > 0,
      dropdownColumns: true,
      dropdownItems: [
        { label: 'all insights', href: '/insights' },
        ...insightsMenu.map(entry => ({
          label: entry.label,
          href: entry.href,
        })),
      ],
    },
    {
      label: 'search by',
      href: '/search-by',
      hasDropdown: true,
      dropdownItems: [
        {
          label: 'health categories',
          href: '/health-categories',
        },
        { label: 'brands', href: '/brands' },
        { label: 'suppliers', href: '/suppliers' },
        { label: 'ingredients', href: '/ingredients' },
        { label: 'events', href: '/events' },
      ],
    },
    { label: 'about', href: '/about-us', hasDropdown: false },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutside = Object.values(dropdownRefs.current).every(ref => !ref?.contains(target));
      if (isOutside) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (label: string) => {
    setOpenDropdown(prev => (prev === label ? null : label));
  };

  const toggleMobileDropdown = (label: string) => {
    setOpenMobileDropdown(prev => (prev === label ? null : label));
  };

  const socialLinks = [
    {
      href: import.meta.env.VITE_SOCIAL_FACEBOOK,
      icon: <FacebookIcon size={16} className='h-4 w-4' />,
    },
    {
      href: import.meta.env.VITE_SOCIAL_INSTAGRAM,
      icon: <InstagramIcon size={16} className='h-4 w-4' />,
    },
    {
      href: import.meta.env.VITE_SOCIAL_LINKEDIN,
      icon: <LinkedInIcon size={16} className='h-4 w-4' />,
    },
  ].filter(link => link.href);

  return (
    <header className='bg-primary z-50 w-full'>
      <nav className='mx-auto max-w-7xl'>
        <div className='flex items-center justify-between px-4 py-3'>
          {/* Home Icon  */}
          <div className='flex items-center gap-8'>
            <a
              href='/'
              className='hover:bg-primary/80 flex shrink-0 items-center justify-center rounded-md p-2 text-white transition-colors'
            >
              <HomeIcon size={24} className='h-6 w-6' />
            </a>

            {/* Desktop Navigation */}
            <div className='hidden md:flex md:items-center md:gap-2'>
              {links.map(link =>
                link.hasDropdown ? (
                  <div
                    key={link.href}
                    className='relative'
                    ref={el => {
                      dropdownRefs.current[link.label] = el;
                    }}
                  >
                    <button
                      onClick={() => toggleDropdown(link.label)}
                      className='flex items-center px-3 py-2 text-sm font-medium tracking-wide text-white transition-colors hover:text-white/80'
                    >
                      {link.label}
                      <ChevronDownIcon
                        size={16}
                        className={`ml-1 h-4 w-4 transition-transform duration-200 ${openDropdown === link.label ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {openDropdown === link.label && (
                      <div
                        className={`ring-opacity-5 absolute top-full left-0 z-50 mt-1 min-w-48 rounded-md bg-white py-2 shadow-lg ring-1 ring-blue-100 ${
                          link.dropdownColumns
                            ? 'grid max-w-[calc(100vw-2rem)] auto-cols-[minmax(11rem,max-content)] grid-flow-col grid-rows-[repeat(7,auto)] gap-x-2 overflow-x-auto'
                            : ''
                        }`}
                      >
                        {link.dropdownItems?.map(item => (
                          <a
                            key={item.label}
                            href={item.href}
                            className='hover:bg-primary/10 hover:text-primary block px-4 py-2 text-sm whitespace-nowrap text-gray-700'
                          >
                            {item.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className='flex items-center px-3 py-2 text-sm font-medium tracking-wide text-white transition-colors hover:text-white/80'
                  >
                    {link.label}
                  </a>
                )
              )}
            </div>
          </div>

          {/* Right Group: Search Bar + Social Icons */}
          <div className='hidden items-center gap-15 md:flex'>
            {/* Search Bar */}
            <SearchAutocomplete className='w-full max-w-md' />

            {/* Social Icons */}
            {socialLinks.length > 0 && (
              <div className='flex items-center gap-2'>
                {socialLinks.map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-primary flex h-8 w-8 items-center justify-center rounded-full bg-white transition-colors hover:bg-white/90'
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            )}

            {/* Login Icon */}
            <a
              href={`${B2B_PORTAL_URL}/auth/login`}
              className='hover:bg-primary/80 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white transition-colors'
            >
              <LoginIcon size={18} className='h-4.5 w-4.5' />
              <span>Login</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            type='button'
            className='hover:bg-primary/80 inline-flex items-center justify-center rounded-lg p-2 text-white transition-colors md:hidden'
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <CloseIcon size={24} className='h-6 w-6' />
            ) : (
              <MenuIcon size={24} className='h-6 w-6' />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out md:hidden ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <div className='border-primary/40 space-y-1 border-t py-4'>
            <div className='px-4 pb-3'>
              <SearchAutocomplete
                inputClassName='py-2'
                onNavigate={() => setIsMobileMenuOpen(false)}
              />
            </div>
            {links.map(link =>
              link.hasDropdown ? (
                <div key={link.href}>
                  <button
                    onClick={() => toggleMobileDropdown(link.label)}
                    className='hover:bg-primary/80 flex w-full items-center justify-between px-4 py-3 text-base font-medium text-white'
                  >
                    {link.label}
                    <ChevronDownIcon
                      size={16}
                      className={`ml-1 h-4 w-4 transition-transform duration-200 ${openMobileDropdown === link.label ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-200 ${openMobileDropdown === link.label ? 'max-h-96' : 'max-h-0'}`}
                  >
                    {link.dropdownItems?.map(item => (
                      <a
                        key={item.label}
                        href={item.href}
                        className='bg-primary/80 hover:bg-primary/90 block py-2 pr-4 pl-8 text-sm text-white'
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className='hover:bg-primary/80 block px-4 py-3 text-base font-medium text-white'
                >
                  {link.label}
                </a>
              )
            )}
            <a
              href={`${B2B_PORTAL_URL}/auth/login`}
              className='hover:bg-primary/80 flex items-center gap-2 px-4 py-3 text-base font-medium text-white'
            >
              <LoginIcon size={20} className='h-5 w-5' />
              Login
            </a>
            {socialLinks.length > 0 && (
              <div className='border-primary/40 flex justify-center gap-3 border-t px-4 pt-4'>
                {socialLinks.map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-primary flex h-8 w-8 items-center justify-center rounded-full bg-white transition-colors hover:bg-white/80'
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};
